import type * as recall from '@otter-ai/recall-client';
import type { MinimalListGroup } from '@otter-ai/recall-client';
import { ErrorType, Screen } from '@otter-ai/telemetry';
import type { BatchItem } from '@rpldy/shared';
import type { Subscription } from '@/shared/types/common';
import { getQueryClient } from '@/shared/utils/queryCreators';
import { displayErrorToast } from '../Components/Toasts';
import { importCheckingEvent, importFailedEvent } from '../tracking/tracking';
import type { ImportQuotas } from '../types/types';

const BIZ_CC_TRIAL_IMPORT_LIMIT = 20;

export type UploadContext =
  | { type: 'home' }
  | { type: 'folder'; id: string }
  | { type: 'group'; id: string };

/**
 * Get upload context from pathname.
 * Simple pattern matching useHeaderLayout approach.
 *
 * @example
 * getUploadContext('/home') // { type: 'home' }
 * getUploadContext('/folder/123') // { type: 'folder', id: '123' }
 * getUploadContext('/group/456') // { type: 'group', id: '456' }
 */
export function getUploadContext(pathname: string): UploadContext {
  const segments = pathname.split('/');
  const route = segments[1];
  const id = segments[2];

  if (route === 'group' && id) {
    return { type: 'group', id };
  }

  if (route === 'folder' && id) {
    return { type: 'folder', id };
  }

  return { type: 'home' };
}

export function parseXmlResponse(item: BatchItem): {
  bucket: string | null;
  key: string | null;
} {
  const body = item.uploadResponse?.data ?? item.uploadResponse?.response;

  if (typeof DOMParser === 'undefined') {
    return { bucket: null, key: null };
  }

  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(body, 'text/xml');

    const bucketNode = xmlDoc.getElementsByTagName('Bucket')[0];
    const keyNode = xmlDoc.getElementsByTagName('Key')[0];

    const bucket = bucketNode?.textContent || null;
    const key = keyNode?.textContent || null;

    return { bucket, key };
  } catch (_error) {
    return { bucket: null, key: null };
  }
}

export function extractUploadParams(
  speechUploadParams: recall.CreateSpeechUploadResponse['data'] | undefined,
) {
  if (!speechUploadParams) return null;

  const raw = speechUploadParams;
  if (!raw) return null;

  const url = raw.form_action;
  if (!url) return null;

  const { form_action, ...fields } = raw;

  return { url, fields };
}

// Extracts upload quotas from the response
export function extractUploadQuotas(
  data: recall.CreateSpeechUploadResponses[200] | undefined,
  error: Error | null,
): ImportQuotas {
  // Handle error cases and transform responses
  if (error && 'code' in error) {
    const errorCode = error.code;

    // Basic Account has exhausted imports
    if (errorCode === (44 as recall.ErrorCode)) {
      return {
        importQuota: 0,
        importMax: 3,
        monthlyImportQuota: null,
        monthlyImportMax: null,
        outOfMinutes: false,
      };
    }
    // Pro Account has exhausted imports
    if (errorCode === (63 as recall.ErrorCode)) {
      return {
        importQuota: null,
        importMax: null,
        monthlyImportQuota: 0,
        monthlyImportMax: 10,
        outOfMinutes: false,
      };
    }
    // Out of minutes case
    if (errorCode === (26 as recall.ErrorCode)) {
      displayErrorToast('You have used all of your minutes');
      return {
        importQuota: null,
        importMax: null,
        monthlyImportQuota: null,
        monthlyImportMax: null,
        outOfMinutes: true,
      };
    }
    // External ASR error case
    if (errorCode === (93 as recall.ErrorCode)) {
      return {
        importQuota: data?.import_quota ?? null,
        importMax: data?.import_max ?? null,
        monthlyImportQuota: data?.monthly_import_quota ?? null,
        monthlyImportMax: data?.monthly_import_max ?? null,
        outOfMinutes: false,
        externalASRError: true,
      };
    }
  }

  // Success case
  return {
    importQuota: data?.import_quota ?? null,
    importMax: data?.import_max ?? null,
    monthlyImportQuota: data?.monthly_import_quota ?? null,
    monthlyImportMax: data?.monthly_import_max ?? null,
    outOfMinutes: false,
  };
}

// TODO: Migrate to MIME types
// https://aisense.atlassian.net/browse/OTWEB-11140
export function getFileExtension(name: string): string {
  const idx = name.lastIndexOf('.');
  return idx > 0 ? name.slice(idx + 1) : '';
}

// get media duration in seconds
export const getMediaDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    if (
      typeof URL === 'undefined' ||
      typeof document === 'undefined' ||
      typeof URL.createObjectURL !== 'function'
    ) {
      reject(new Error('Media duration is only available in the browser'));
      return;
    }

    const url = URL.createObjectURL(file);
    const media = document.createElement('audio');

    let cleaned = false;
    let resolved = false;

    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      URL.revokeObjectURL(url);
      media.remove();
    };

    media.preload = 'metadata';
    media.src = url;

    media.onloadedmetadata = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve(media.duration);
    };

    media.onerror = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      reject(new Error('Failed to load media metadata'));
    };
  });
};

export function isExtensionAllowed(
  ext: string,
  allowedImportFormats?: string[],
): boolean {
  if (!ext) {
    return false;
  }

  if (!allowedImportFormats || allowedImportFormats.length === 0) {
    return true;
  }

  return allowedImportFormats.some(
    (allowed) => allowed.toLowerCase() === ext.toLowerCase(),
  );
}

export async function validateDuration(
  item: BatchItem,
  maxRecordingDuration?: number,
) {
  const file = item.file;

  if (file instanceof File && maxRecordingDuration) {
    try {
      const duration = await getMediaDuration(file);
      if (duration > maxRecordingDuration) {
        displayErrorToast(
          `The duration of the recording you attempted to import has exceeded the maximum limit of ${Math.round(maxRecordingDuration / 3600)} hours. Please try again with a shorter recording.`,
        );
        importFailedEvent(item, ErrorType.durationLimit);
        return false;
      }
      importCheckingEvent(item, duration);
      return true;
    } catch (_error) {
      return true;
    }
  }
}

// validate file extension and duration
async function validateBatchItem(
  item: BatchItem,
  allowedImportFormats?: string[],
  maxRecordingDuration?: number,
) {
  const file = item.file;
  const ext = getFileExtension(file.name);

  if (!isExtensionAllowed(ext, allowedImportFormats)) {
    displayErrorToast(
      "That file type isn't supported. Select an AAC, MP3, M4A, WMA, MOV, MPEG, MP4, or WMV file and upload it again.",
    );
    importFailedEvent(item, ErrorType.InvalidFileExtension);
    return null;
  }

  const validateDurationResult = await validateDuration(
    item,
    maxRecordingDuration,
  );

  if (!validateDurationResult) {
    return null;
  }
  return item;
}

export async function validateBatchItems(
  items: BatchItem[],
  abortFn: (itemId: string) => void,
  allowedImportFormats?: string[],
  maxRecordingDuration?: number,
): Promise<BatchItem[]> {
  const validationPromises = items.map((item) =>
    validateBatchItem(item, allowedImportFormats, maxRecordingDuration),
  );

  const validationResults = await Promise.all(validationPromises);

  // Check each validation result and abort items that failed validation
  const validItems: BatchItem[] = [];
  validationResults.forEach((result, index) => {
    if (result === null) {
      abortFn(items[index].id);
    } else {
      validItems.push(result);
    }
  });

  return validItems;
}

export function hasRemainingImportQuota(quota: ImportQuotas): boolean {
  return Boolean(quota.importQuota) || Boolean(quota.monthlyImportQuota);
}

export function isLifetimeImportLimitApplicable(quota: ImportQuotas): boolean {
  return quota.importMax !== null;
}

export function isMonthlyImportLimitApplicable(quota: ImportQuotas): boolean {
  return quota.monthlyImportMax !== null;
}

export function isOutOfMinutes(quota: ImportQuotas): boolean {
  return Boolean(quota.outOfMinutes);
}

// Basic and Pro users have import restrictions
export function hasImportRestrictions(quota: ImportQuotas): boolean {
  return (
    isLifetimeImportLimitApplicable(quota) ||
    isMonthlyImportLimitApplicable(quota)
  );
}

function wouldExceedRemainingQuota(
  remainingQuota: number,
  itemCount: number,
  currentStagingCount: number,
): boolean {
  return itemCount > remainingQuota - currentStagingCount;
}

export function wouldExceedImportQuota(
  uploadQuota: ImportQuotas,
  itemCount: number,
  currentStagingCount = 0,
): boolean {
  if (!hasImportRestrictions(uploadQuota)) {
    return false;
  }

  if (isOutOfMinutes(uploadQuota)) {
    return true;
  }

  // Check lifetime import quota - need to check for null explicitly, not falsy
  if (uploadQuota.importQuota !== null) {
    if (
      wouldExceedRemainingQuota(
        uploadQuota.importQuota,
        itemCount,
        currentStagingCount,
      )
    ) {
      return true;
    }
  }

  // Check monthly import quota - need to check for null explicitly, not falsy
  if (uploadQuota.monthlyImportQuota !== null) {
    if (
      wouldExceedRemainingQuota(
        uploadQuota.monthlyImportQuota,
        itemCount,
        currentStagingCount,
      )
    ) {
      return true;
    }
  }

  return false;
}

export function importQuotaText(quota: ImportQuotas) {
  if (isLifetimeImportLimitApplicable(quota)) {
    return `${quota.importQuota} of ${quota.importMax} imports left`;
  }
  return `${quota.monthlyImportQuota} of ${quota.monthlyImportMax} imports left`;
}

export function invalidateSubscriptionData() {
  const queryClient = getQueryClient();
  queryClient.invalidateQueries({
    queryKey: ['subscription'],
  });
}

export function hasBizCCTrialHitLimit({
  subscription,
}: {
  subscription?: Subscription;
}): boolean {
  if (!subscription) {
    return false;
  }

  return (
    subscription.reverse_trial?.trial_type === 'cc_team_ws_trial' &&
    subscription.import_usage >= 20
  );
}

export function wouldExceedBizCCTrialLimit({
  subscription,
  itemCount,
  currentStagingCount = 0,
}: {
  subscription?: Subscription;
  itemCount: number;
  currentStagingCount?: number;
}): boolean {
  if (!subscription) {
    return false;
  }

  if (subscription.reverse_trial?.trial_type !== 'cc_team_ws_trial') {
    return false;
  }

  const remainingQuota = BIZ_CC_TRIAL_IMPORT_LIMIT - subscription.import_usage;
  return wouldExceedRemainingQuota(
    remainingQuota,
    itemCount,
    currentStagingCount,
  );
}

function isDmGroup(groupId: string): boolean {
  const queryClient = getQueryClient();
  const cached = queryClient.getQueryData<{ groups: MinimalListGroup[] }>([
    'channels',
  ]);
  const group = cached?.groups?.find((g) => String(g.id) === groupId);
  return !!group && group.is_dm_visible && group.member_count === 2;
}

export function getScreenForContext(
  context: UploadContext,
  pathname: string,
): Screen {
  const route = pathname.split('/')[1];
  if (route === 'setting') {
    return Screen.planPage;
  }
  if (context.type === 'group') {
    return isDmGroup(context.id) ? Screen.dm : Screen.channel;
  }
  if (context.type === 'folder') return Screen.folders;
  return Screen.homeFeed;
}
